import { useState } from "react";
import { NavLink, Navigate, Outlet } from "react-router-dom";
import { BookOpen, CalendarDays, Clock, Image, Lightbulb, ListOrdered, LogOut, Menu, Settings, Sun, UtensilsCrossed, X } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

const LINKS = [
  { to: "/admin", label: "Oggi", icon: Sun, end: true },
  { to: "/admin/calendario", label: "Calendario", icon: CalendarDays },
  { to: "/admin/prenotazioni", label: "Prenotazioni", icon: ListOrdered },
  { to: "/admin/servizi", label: "Servizi e orari", icon: Clock },
  { to: "/admin/date-speciali", label: "Date speciali", icon: CalendarDays },
  { to: "/admin/menu", label: "Menu", icon: UtensilsCrossed },
  { to: "/admin/galleria", label: "Galleria", icon: Image },
  { to: "/admin/diario", label: "Diario", icon: BookOpen, end: true },
  { to: "/admin/diario/idee", label: "Idee lettori", icon: Lightbulb },
  { to: "/admin/contenuti", label: "Contenuti", icon: Settings },
];

export default function AdminLayout() {
  const { user, checked, logout } = useAdminAuth();
  const [open, setOpen] = useState(false);

  if (!checked) {
    return (
      <div className="min-h-screen bg-beige/40 flex items-center justify-center">
        <p className="text-sm text-wood/60">Caricamento…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const nav = (
    <nav className="space-y-1" aria-label="Navigazione area riservata">
      {LINKS.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={() => setOpen(false)}
          data-testid={`admin-nav-${label.toLowerCase().replace(/\s|à/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 text-[0.68rem] uppercase tracking-[0.14em] transition-colors duration-300 ${
              isActive ? "bg-sage text-cream" : "text-cream/70 hover:bg-cream/10"
            }`
          }
        >
          <Icon size={15} strokeWidth={1.5} /> {label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-beige/40 lg:flex">
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:shrink-0 bg-wood-deep grain p-5">
        <div className="px-2 pb-6">
          <p className="font-serif text-xl text-cream">Osteria Aterrana</p>
          <p className="mt-1 text-[0.6rem] uppercase tracking-[0.2em] text-gold">Pannello</p>
        </div>
        {nav}
        <div className="mt-auto px-2 pt-6 border-t border-cream/10">
          <p className="text-xs text-cream/45 break-all">{user.email}</p>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout"
            className="mt-3 inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.16em] text-cream/70 hover:text-cream"
          >
            <LogOut size={13} /> Esci
          </button>
        </div>
      </aside>

      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-wood-deep px-5 py-4">
        <span className="font-serif text-lg text-cream">Osteria Aterrana</span>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Chiudi menu" : "Apri menu"}
          data-testid="admin-mobile-menu"
          className="text-cream p-1"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>
      {open && (
        <div className="lg:hidden bg-wood-deep px-3 pb-5">
          {nav}
          <button
            type="button"
            onClick={logout}
            className="mt-4 flex items-center gap-2 px-4 text-[0.65rem] uppercase tracking-[0.16em] text-cream/70"
          >
            <LogOut size={13} /> Esci
          </button>
        </div>
      )}

      <main className="flex-1 min-w-0 p-4 md:p-8 lg:p-10">
        <Outlet />
      </main>
    </div>
  );
}
